import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { MdUpgrade } from 'react-icons/md';
import { Button, PromoBanner, PROMO_BANNER_DENSITY_COMPACT } from '@aibuild/ui';
import type { AppManifest } from '@api/apps';
import { LATEST_KIT_VERSION } from '../../../appKit/host';
import { builderPath } from '../builder/userAppStatus';

interface KitUpdateNoticeProps {
  appKey: string;
  manifest: AppManifest;
  /** In the builder: hands the migration to the assistant. Elsewhere the notice leads to the builder. */
  onAskAssistant?: () => void;
}

/** Shown only to an app's owner, while its page targets an older App Kit than the latest. */
const KitUpdateNotice: React.FC<KitUpdateNoticeProps> = ({ appKey, manifest, onAskAssistant }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const kitVersion = manifest.presentation?.kitVersion ?? 1;
  if (kitVersion >= LATEST_KIT_VERSION) return null;
  return (
    <PromoBanner
      density={PROMO_BANNER_DENSITY_COMPACT}
      icon={<MdUpgrade />}
      title={intl.formatMessage({
        id: 'apps.kitUpdate.notice',
        defaultMessage: 'A newer version of the app kit is available. Update the app and publish it to use it.',
      })}
      actions={
        onAskAssistant ? (
          <Button variant="outlined" size="sm" onClick={onAskAssistant} dataTestId="app-run__kit-update-ask">
            {intl.formatMessage({ id: 'apps.kitUpdate.ask', defaultMessage: 'Ask the assistant to update' })}
          </Button>
        ) : (
          <Button variant="outlined" size="sm" onClick={() => navigate(builderPath(appKey))} dataTestId="app-run__kit-update-edit">
            {intl.formatMessage({ id: 'apps.kitUpdate.edit', defaultMessage: 'Edit to update' })}
          </Button>
        )
      }
      dataTestId="app-run__kit-update"
    />
  );
};

export default KitUpdateNotice;
